import { create } from "zustand";
import { AnimationAction, Object3D, Scene, Camera, WebGLRenderer } from "three";
import { OrbitControls } from "three-stdlib";

export type ControlState = {
  minDistance: number;
  maxDistance: number;
  minPolarAngle: number;
  maxPolarAngle: number;
  minAzimuthAngle: number;
  maxAzimuthAngle: number;
};

export type ControlsConfig = {
  controls: ControlState;
  // Add more properties here in the future if needed
  // e.g., animations?: {}, transitions?: {}, etc.
};

export type ThreeState = {
  gl: WebGLRenderer | null;
  scene: Scene | null;
  camera: Camera | null;
};

export type ExperienceStoreState = {
  three: ThreeState;
  sceneGroup: Object3D | null;
  actions: { [key: string]: AnimationAction | null } | null; // <- dictionary
  canvas: { domElement: HTMLCanvasElement | null };
  orbitControls: OrbitControls | null;
  pre: ControlsConfig;
  post: ControlsConfig;
  scenePosition: { x: number; y: number; z: number };

  setThree: (state: ThreeState) => void;
  setSceneGroup: (sceneGroup: Object3D | null) => void;
  setCanvas: (canvasUpdate: { domElement: HTMLCanvasElement | null }) => void;
  setOrbitControls: (orbitControls: OrbitControls | null) => void;
  setPre: (update: Partial<ControlsConfig>) => void;
  setPost: (update: Partial<ControlsConfig>) => void;
  setScenePosition: (position: Partial<{ x: number; y: number; z: number }>) => void;
  setModel: (
    scene: Object3D,
    actions: { [key: string]: AnimationAction | null } | null
  ) => void; // <- changed
};

export const useExperienceStore = create<ExperienceStoreState>((set) => ({
  three: { gl: null, scene: null, camera: null },
  sceneGroup: null,
  actions: null,
  orbitControls: null,
  canvas: { domElement: null },
  scenePosition: { x: 0, y: 0, z: 0 },

  pre: {
    controls: {
      minDistance: 0,
      maxDistance: 0,
      minPolarAngle: Math.PI * 0.5,
      maxPolarAngle: Math.PI * 0.5,
      minAzimuthAngle: -Infinity,
      maxAzimuthAngle: Infinity,
    },
  },

  post: {
    controls: {
      minDistance: 2.25, 
      maxDistance: 8, 
      minPolarAngle: 0,
      maxPolarAngle: Math.PI * 0.54,
      minAzimuthAngle: -Infinity,
      maxAzimuthAngle: Infinity,
    },
  },

  setThree: (three) => set({ three }),
  setSceneGroup: (sceneGroup) => set({ sceneGroup }),
  setCanvas: (canvasUpdate) => set({ canvas: canvasUpdate }),
  setOrbitControls: (orbitControls) => set({ orbitControls }),
  setScenePosition: (position) =>
    set((state) => ({ scenePosition: { ...state.scenePosition, ...position } })),

  setPre: (update) =>
    set((state) => ({
      pre: {
        ...state.pre,
        ...update,
        ...(update.controls && {
          controls: { ...state.pre.controls, ...update.controls },
        }),
      },
    })),

  setPost: (update) =>
    set((state) => ({
      post: {
        ...state.post,
        ...update,
        ...(update.controls && {
          controls: { ...state.post.controls, ...update.controls },
        }),
      },
    })),

  setModel: (scene, actions) =>
    set(() => ({
      sceneGroup: scene,
      actions,
    })),

}));
